import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import {
  generateIndependentSelectorCreatorsFor,
  generateLoadStatusSelectorsFor,
  generateSelectorCreatorsDependentOn,
  generateSelectorsDependentOn
} from '@aviary/store/store.selector-generators';
import {ItemRequestsState} from '@store/item-requests/item-requests.state';
import {CANCELABLE_STATUSES, ITEM_REQUESTS_FEATURE_KEY, ItemRequest} from '@store/item-requests/item-requests.types';
import {FALLBACK_ITEM_REQUEST, FALLBACK_ITEM_REQUEST_COMMENT} from './item-requests.fallback';
import {FALLBACK_CATALOG_RECORD} from '@store/catalog/catalog.fallback';
import {createStringSelector} from '@aviary/store/store.helpers';
import {CatalogRecord} from '@store/catalog/catalog.types';
import {hasValue} from '@aviary/store/common/typing.helpers';

export namespace ItemRequestsSelectors {
  export const entireState: MxSelector<ItemRequestsState> = createFeatureSelector(ITEM_REQUESTS_FEATURE_KEY);

  export namespace Stateless {
    export const {
      createdFor,
      titleFor,
      authorFor,
      hasLinkedCatalogRecordFor,
      linkedCatalogRecordFor,
      statusFor,
      hasUnreadStaffCommentsFor,
      holdPlacedFor,
      commentsFor
    } = generateIndependentSelectorCreatorsFor(FALLBACK_ITEM_REQUEST, NO_NAME_PREFIX);

    export const {
      commentCommentFor,
      commentCreatedFor,
      commentPatronIdFor,
      commentUserIdFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_ITEM_REQUEST_COMMENT, 'comment');

    export const holdNotPlacedFor: MxSelectorCreator<[ItemRequest], boolean> =
      itemRequest => createSelector(
        holdPlacedFor(itemRequest),
        holdPlaced => !holdPlaced
      );

    export const {
      linkedCatalogRecordAuthorFor,
      linkedCatalogRecordTitleFor
    } = generateSelectorCreatorsDependentOn(linkedCatalogRecordFor, FALLBACK_CATALOG_RECORD, 'linkedCatalogRecord');

    export const calculatedAuthorFor: MxSelectorCreator<[ItemRequest], string> =
      (itemRequest) => createStringSelector(
        hasLinkedCatalogRecordFor(itemRequest),
        linkedCatalogRecordAuthorFor(itemRequest),
        authorFor(itemRequest),
        (hasCatalogRecord, catalogRecordAuthor, requestAuthor) => hasCatalogRecord ? catalogRecordAuthor : requestAuthor
      );

    export const calculatedTitleFor: MxSelectorCreator<[ItemRequest], string> =
      (itemRequest) => createStringSelector(
        hasLinkedCatalogRecordFor(itemRequest),
        linkedCatalogRecordTitleFor(itemRequest),
        titleFor(itemRequest),
        (hasCatalogRecord, catalogRecordTitle, requestTitle) => hasCatalogRecord ? catalogRecordTitle : requestTitle
      );
  } // namespace Stateless

  export namespace Current {
    const subState: MxSelector<ItemRequestsState['current']> = createSelector(
      entireState,
      state => state.current
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      finishedLoading,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const itemRequest: MxSelector<ItemRequest> = createSelector(
      subState,
      ({data}) => data ?? FALLBACK_ITEM_REQUEST
    );

    export const {
      id,
      organizationId,
      requestedBranchId,
      patronId,
      status,
      title,
      author,
      isbn,
      materialTypeDesc,
      notes,
      hasUnreadStaffComments,
      active,
      created,
      modified,
      comments,
      pickupBranchName,
      linkedCatalogRecord,
      hasLinkedCatalogRecord,
      holdPlaced,
      decisionDate,
      printType,
      yearOfPublication
    } = generateSelectorsDependentOn(itemRequest, FALLBACK_ITEM_REQUEST, NO_NAME_PREFIX);

    export const {
      linkedCatalogRecordId,
      linkedCatalogRecordAuthor,
      linkedCatalogRecordTitle,
      linkedCatalogRecordAvailability
    } = generateSelectorsDependentOn(linkedCatalogRecord, FALLBACK_CATALOG_RECORD, 'linkedCatalogRecord');

    export const {
      availabilityAvailableCount,
      availabilityOnHoldCount
    } = generateSelectorsDependentOn(linkedCatalogRecordAvailability, FALLBACK_CATALOG_RECORD.availability, 'availability');

    export const cancelable: MxSelector<boolean> = createSelector(
      status,
      (status) => CANCELABLE_STATUSES.includes(status)
    );
  } // namespace Current

  export namespace ActiveItemRequests {
    const substate: MxSelector<ItemRequestsState['activeItemRequests']> = createSelector(
      entireState,
      ({activeItemRequests}) => activeItemRequests
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(substate);

    export const activeItemRequests: MxSelector<ItemRequest[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const requestCount: MxSelector<number> = createSelector(
      substate,
      ({requestCount}) => requestCount
    );
  } // namespace ActiveItemRequests

  export namespace ClosedItemRequests {
    const substate: MxSelector<ItemRequestsState['closedItemRequests']> = createSelector(
      entireState,
      ({closedItemRequests}) => closedItemRequests
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(substate);

    export const closedItemRequests: MxSelector<ItemRequest[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const requestCount: MxSelector<number> = createSelector(
      substate,
      ({requestCount}) => requestCount
    );

    export const linkedCatalogRecords: MxSelector<CatalogRecord[]> = createSelector(
      closedItemRequests,
      requests => requests.map(itemRequest => itemRequest.linkedCatalogRecord).filter(hasValue)
    );

    export const linkedCatalogRecordIds: MxSelector<number[]> = createSelector(
      linkedCatalogRecords,
      records => records.map(record => record.id)
    );

    export const {
      holdPlacedFor,
      holdNotPlacedFor
    } = Stateless;
  } // namespace ClosedItemRequests
}
